import { Component, type ReactNode, useState, useEffect } from 'react'
import { FormattedMessage } from 'react-intl'
import styles from './WebGLErrorBoundary.module.css'

function detectWebGLSupport(): boolean {
  try {
    const canvas = document.createElement('canvas')
    const gl =
      canvas.getContext('webgl2') ??
      canvas.getContext('webgl') ??
      canvas.getContext('experimental-webgl')
    return gl !== null
  } catch {
    return false
  }
}

function WebGLFallback() {
  return (
    <div className={styles.fallback}>
      <svg
        className={styles.icon}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
      >
        <path d="M21 7.5V18a2.25 2.25 0 0 1-2.25 2.25H5.25A2.25 2.25 0 0 1 3 18V7.5m18 0A2.25 2.25 0 0 0 18.75 5.25H5.25A2.25 2.25 0 0 0 3 7.5m18 0-4.28 4.28a2.25 2.25 0 0 1-3.18 0L12 10.24l-1.54 1.54a2.25 2.25 0 0 1-3.18 0L3 7.5" />
      </svg>
      <p className={styles.message}>
        <FormattedMessage
          id="webgl.unsupported.message"
          defaultMessage="3D visualization is not available on this device"
        />
      </p>
      <p className={styles.hint}>
        <FormattedMessage
          id="webgl.unsupported.hint"
          defaultMessage="Try opening on a different device"
        />
      </p>
    </div>
  )
}

/** Generic failure (e.g. a 3D model failed to load). Offers a page reload. */
function GenericErrorFallback() {
  return (
    <div className={styles.fallback}>
      <svg
        className={styles.icon}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
      >
        <path d="M12 9v3.75m0 3.75h.008M10.34 3.94 1.94 18a1.5 1.5 0 0 0 1.3 2.25h17.52A1.5 1.5 0 0 0 22.06 18l-8.4-14.06a1.5 1.5 0 0 0-2.58 0Z" />
      </svg>
      <p className={styles.message}>
        <FormattedMessage
          id="viewer.error.message"
          defaultMessage="Something went wrong"
        />
      </p>
      <p className={styles.hint}>
        <FormattedMessage
          id="viewer.error.hint"
          defaultMessage="Please refresh the page and try again"
        />
      </p>
      <button
        type="button"
        className={styles.reloadButton}
        onClick={() => window.location.reload()}
      >
        <FormattedMessage id="viewer.error.reload" defaultMessage="Refresh" />
      </button>
    </div>
  )
}

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
  isWebGLError: boolean
}

class WebGLRenderBoundary extends Component<Props, State> {
  state: State = { hasError: false, isWebGLError: false }

  static getDerivedStateFromError(error: Error): State {
    // Catch every render error in the 3D subtree — including GLB model load /
    // parse failures — so a single failed asset never crashes the whole page.
    return {
      hasError: true,
      isWebGLError: Boolean(error.message?.includes('WebGL')),
    }
  }

  componentDidCatch(error: Error) {
    console.error('3D viewer error:', error)
  }

  render() {
    if (this.state.hasError) {
      return this.state.isWebGLError ? (
        <WebGLFallback />
      ) : (
        <GenericErrorFallback />
      )
    }
    return this.props.children
  }
}

export function WebGLErrorBoundary({ children }: Props) {
  const [webGLSupported, setWebGLSupported] = useState<boolean | null>(null)

  useEffect(() => {
    setWebGLSupported(detectWebGLSupport())
  }, [])

  if (webGLSupported === null) return null
  if (!webGLSupported) return <WebGLFallback />

  return <WebGLRenderBoundary>{children}</WebGLRenderBoundary>
}
